import type { Seat } from '@campfire/engine';
import { FLEET, SIZE, placementError, shipOrientation, squareName, type BattleshipView, type Shot, type ShipView } from '@campfire/game-battleship';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';
import { gridBoard } from './grid-board.ts';

const shipName = (id: number): string => FLEET[id]?.name ?? 'ship';
const poss = (ctx: TableCtx, s: Seat): string => (s === ctx.you ? 'your' : `${name(ctx, s)}’s`);

/** Sentence for the last volley, from the viewer's point of view. */
function lastText(ctx: TableCtx, v: BattleshipView): string | null {
  const l = v.last;
  if (!l) return null;
  const foe = 1 - l.seat;
  const who = l.seat === ctx.you ? 'You' : name(ctx, l.seat);
  const parts = l.shots.map((s) => {
    const at = squareName(s.sq);
    if (s.result === 'miss') return `${at}: miss`;
    if (s.result === 'hit') return `${at}: hit!`;
    return foe === ctx.you ? `${at}: "You sank my ${shipName(s.ship as number)}!"` : `${at}: sank ${poss(ctx, foe)} ${shipName(s.ship as number)}!`;
  });
  return `${who} fired ${parts.join(', ')}`;
}

function statusText(ctx: TableCtx, v: BattleshipView, me: Seat | null, placed: number, chosen: number): string {
  if (v.result) {
    const w = v.result.winners[0] as Seat;
    return `${w === ctx.you ? 'You win' : `${name(ctx, w)} wins`}: ${v.result.reason ?? ''}`;
  }
  if (v.phase === 'setup') {
    if (me === null) return 'Both players are hiding their fleets.';
    if (v.ready[me]) return `Fleet locked in. Waiting for ${name(ctx, 1 - me)}…`;
    return placed < FLEET.length ? 'Place your fleet: tap a ship, then a square.' : 'Fleet in position. Press Ready when you are happy.';
  }
  const turn = v.turn as Seat;
  if (turn === me) return v.volley === 1 ? 'Your turn: tap a square in Their waters, then Fire.' : `Your turn: pick ${v.volley} squares (${chosen} chosen), then Fire.`;
  return `${name(ctx, turn)} ${turn === ctx.you ? 'are' : 'is'} taking aim…`;
}

/** Which edges of a ship cell border open water, so a ship can be outlined as one piece. */
function edges(ship: ShipView, i: number): string {
  const has = (j: number) => ship.cells.includes(j);
  const x = i % SIZE;
  const out: string[] = [];
  if (!has(i - SIZE) || i < SIZE) out.push('et');
  if (!has(i + SIZE) || i + SIZE >= SIZE * SIZE) out.push('eb');
  if (!has(i - 1) || x === 0) out.push('el');
  if (!has(i + 1) || x === SIZE - 1) out.push('er');
  return out.join(' ');
}

interface BoardArgs {
  readonly v: BattleshipView;
  /** Whose waters this board shows. */
  readonly seat: Seat;
  readonly onTap?: (i: number) => void;
  readonly targets?: ReadonlySet<number>;
}

function board(a: BoardArgs) {
  const { v, seat } = a;
  const incoming = new Map<number, Shot>(v.shots[1 - seat]!.map((s) => [s.sq, s]));
  const ships = new Map<number, ShipView>();
  for (const s of v.fleets[seat] ?? []) for (const c of s.cells) ships.set(c, s);
  const lastHere = new Set(v.last && v.last.seat !== seat ? v.last.shots.map((s) => s.sq) : []);
  return gridBoard({
    size: SIZE,
    flip: false,
    topDown: true,
    coords: true,
    testid: `bs-board-${seat}`,
    cell: ({ index: i }) => {
      const ship = ships.get(i);
      const shot = incoming.get(i);
      const tag = squareName(i);
      const cls = [
        'bs-sq',
        ship ? `ship s${ship.id}${ship.sunk ? ' sunk' : ''} ${edges(ship, i)}` : '',
        shot ? shot.result : '',
        shot && !ship && shot.result !== 'miss' ? 'unseen' : '',
        a.targets?.has(i) ? 'target' : '',
        lastHere.has(i) ? 'latest' : '',
      ].filter(Boolean).join(' ');
      const state = shot ? (shot.result === 'miss' ? 'miss' : shot.result === 'hit' ? 'hit' : 'sunk') : ship ? 'ship' : 'water';
      return {
        classes: cls,
        label: `${tag}, ${state}`,
        attrs: { 'data-testid': `bs-b${seat}-${tag}`, 'data-sq': tag, 'data-state': state, ...(ship ? { 'data-ship': ship.id } : {}) },
        ...(a.onTap ? { onTap: () => (a.onTap as (i: number) => void)(i) } : {}),
        content: shot ? h(`span.bs-mark.${shot.result === 'miss' ? 'miss' : 'hit'}`, {}, shot.result === 'miss' ? '' : '✹') : null,
      };
    },
  });
}

function fleetChips(ctx: TableCtx, v: BattleshipView, seat: Seat) {
  const sunk = new Set((v.fleets[seat] ?? []).filter((s) => s.sunk).map((s) => s.id));
  return h(
    'div.bs-player',
    { 'data-testid': `bs-player-${seat}`, 'data-seat': seat },
    h('span.bs-pname', {}, name(ctx, seat)),
    h('span.muted', { 'data-testid': `bs-afloat-${seat}`, 'data-afloat': v.afloat[seat] ?? 0 }, `${v.afloat[seat]} afloat`),
    h('span.bs-chips', {}, FLEET.map((f, id) => h(`span.bs-chip${sunk.has(id) ? '.sunk' : ''}`, { title: f.name, 'data-testid': `bs-chip-${seat}-${id}` }, f.name.slice(0, 1) + f.length))),
  );
}

export const battleshipUi: GameUi = {
  id: 'battleship',
  // Fleets are secret: pass & play shows a hand-off screen between players.
  privateHands: true,
  seatInfo: (ctx, s) => `${(ctx.msg.view as BattleshipView).afloat[s] ?? 0} afloat`,
  render(ctx) {
    const v = ctx.msg.view as BattleshipView;
    const me = ctx.mySeat;
    const over = v.phase === 'over';
    const sig = `${v.phase}-${v.moves}-${v.ready.join()}`;
    if (ctx.local.get<string>('bs-sig') !== sig) {
      ctx.local.set('bs-sig', sig);
      ctx.local.set('bs-targets', []);
      ctx.local.set('bs-note', '');
      ctx.local.set('bs-resign', false);
      ctx.local.set('bs-tab', 'their');
    }
    const tab = ctx.local.get<string>('bs-tab') === 'mine' ? 'mine' : 'their';
    const targets = ctx.local.get<number[]>('bs-targets') ?? [];
    const note = ctx.local.get<string>('bs-note') ?? '';
    const mineShips = me === null ? [] : (v.fleets[me] ?? []);
    const placedIds = new Set(mineShips.map((s) => s.id));
    const isSetup = v.phase === 'setup';
    const editing = isSetup && me !== null && !v.ready[me];
    // Selected ship: the player's choice if still valid, else the first one not yet placed.
    const picked = ctx.local.get<number>('bs-sel') ?? -1;
    const sel: number = picked >= 0 && picked < FLEET.length ? picked : FLEET.findIndex((_, id) => !placedIds.has(id));
    const vert = ctx.local.get<boolean>('bs-vert') === true;
    const touching = v.options.touching;

    const say = (t: string) => ctx.local.set('bs-note', t);
    const placeShip = (id: number, cell: number, vertical: boolean, ok = true): boolean => {
      const err = placementError(mineShips, id, cell, vertical, touching);
      if (err) {
        say(ok ? `Can’t go there: ${err}.` : `No room to turn: ${err}.`);
        return false;
      }
      ctx.local.set('bs-vert', vertical);
      ctx.act({ type: 'place', ship: id, cell, vertical });
      return true;
    };
    const setupTap = (i: number) => {
      const at = mineShips.find((s) => s.cells.includes(i));
      if (at && at.id === sel) return rotate();
      if (at) {
        ctx.local.set('bs-sel', at.id);
        ctx.local.set('bs-vert', shipOrientation(at));
        return say('');
      }
      if (sel < 0) return say('Every ship is placed. Tap a ship to move it, or press Ready.');
      say('');
      if (placeShip(sel, i, vert)) {
        const next = FLEET.findIndex((_, id) => id !== sel && !placedIds.has(id));
        ctx.local.set('bs-sel', next >= 0 ? next : sel);
      }
    };
    function rotate() {
      const cur = mineShips.find((s) => s.id === sel);
      if (sel < 0) return;
      if (!cur) {
        ctx.local.set('bs-vert', !vert);
        return;
      }
      say('');
      placeShip(sel, cur.cells[0] as number, !shipOrientation(cur), false);
    }
    const fireTap = (i: number) => {
      const done = new Set(v.shots[me as Seat]!.map((s) => s.sq));
      if (done.has(i)) return;
      if (targets.includes(i)) return ctx.local.set('bs-targets', targets.filter((t) => t !== i));
      if (v.volley === 1) return ctx.local.set('bs-targets', [i]);
      if (targets.length < v.volley) ctx.local.set('bs-targets', [...targets, i]);
    };

    const myTurn = me !== null && v.turn === me && !over;
    const theirSeat: Seat = me === null ? 1 : 1 - me;
    const mySeat: Seat = me === null ? 0 : me;
    const theirBoard = isSetup ? null : board({
      v,
      seat: theirSeat,
      ...(myTurn ? { onTap: fireTap, targets: new Set(targets) } : {}),
    });
    const myBoard = board({ v, seat: mySeat, ...(editing ? { onTap: setupTap } : {}) });

    const tabs = isSetup
      ? null
      : h(
          'div.bs-tabs',
          { role: 'tablist' },
          h(`button.bs-tab${tab === 'their' ? '.on' : ''}`, { 'data-testid': 'bs-tab-their', role: 'tab', onclick: () => ctx.local.set('bs-tab', 'their') }, me === null ? `${name(ctx, theirSeat)} waters` : 'Their waters'),
          h(`button.bs-tab${tab === 'mine' ? '.on' : ''}`, { 'data-testid': 'bs-tab-mine', role: 'tab', onclick: () => ctx.local.set('bs-tab', 'mine') }, me === null ? `${name(ctx, mySeat)} waters` : 'Your fleet'),
        );

    let tray: HTMLElement | null = null;
    if (editing) {
      tray = h(
        'div.bs-tray',
        { 'data-testid': 'bs-tray' },
        FLEET.map((f, id) =>
          h(
            `button.bs-ship-btn${id === sel ? '.sel' : ''}${placedIds.has(id) ? '.placed' : ''}`,
            { 'data-testid': `bs-tray-${id}`, 'data-placed': placedIds.has(id) ? 1 : 0, onclick: () => (ctx.local.set('bs-sel', id), ctx.local.set('bs-vert', mineShips.find((s) => s.id === id) ? shipOrientation(mineShips.find((s) => s.id === id) as ShipView) : vert), say('')) },
            h('span.bs-ship-name', {}, f.name),
            h('span.bs-ship-len', {}, Array.from({ length: f.length }, () => h('i'))),
          ),
        ),
      );
    }

    const setupButtons = editing
      ? h(
          'div.row.actions.bs-actions',
          { 'data-testid': 'bs-setup-actions' },
          h('button', { 'data-testid': 'bs-rotate', disabled: sel < 0, onclick: rotate }, `Rotate (${(mineShips.find((s) => s.id === sel) ? shipOrientation(mineShips.find((s) => s.id === sel) as ShipView) : vert) ? 'down' : 'across'})`),
          h('button', { 'data-testid': 'bs-randomize', onclick: () => ctx.act({ type: 'randomize' }) }, 'Randomize'),
          h('button', { 'data-testid': 'bs-clear', disabled: placedIds.size === 0, onclick: () => (ctx.local.set('bs-sel', 0), ctx.act({ type: 'clear' })) }, 'Clear'),
          h('button.primary', { 'data-testid': 'bs-ready', disabled: placedIds.size < FLEET.length, onclick: () => ctx.act({ type: 'ready' }) }, 'Ready'),
        )
      : null;

    const fireBar = myTurn
      ? h(
          'div.row.actions.bs-actions',
          { 'data-testid': 'bs-fire-bar' },
          h('button.primary.big', { 'data-testid': 'bs-fire', disabled: targets.length !== v.volley, onclick: () => ctx.act({ type: 'fire', squares: targets }) }, v.volley === 1 ? 'Fire!' : `Fire ${targets.length}/${v.volley}`),
        )
      : null;

    let resign: HTMLElement | null = null;
    if (me !== null && !over) {
      const armed = ctx.local.get<boolean>('bs-resign') === true;
      resign = h(
        'div.row.actions.bs-actions',
        { 'data-testid': 'bs-actions' },
        armed
          ? [
              h('button.danger', { 'data-testid': 'bs-resign-confirm', onclick: () => ctx.act({ type: 'resign' }) }, 'Confirm resign'),
              h('button', { 'data-testid': 'bs-resign-cancel', onclick: () => ctx.local.set('bs-resign', false) }, 'Cancel'),
            ]
          : h('button', { 'data-testid': 'bs-resign', onclick: () => ctx.local.set('bs-resign', true) }, 'Resign'),
      );
    }

    const last = isSetup ? null : lastText(ctx, v);
    const readyChips = isSetup
      ? h('div.row.bs-ready-row', {}, [0, 1].map((s) => h(`span.bs-ready-chip${v.ready[s] ? '.on' : ''}`, { 'data-testid': `bs-ready-${s}` }, `${name(ctx, s)}: ${v.ready[s] ? 'ready' : 'placing…'}`)))
      : null;

    return h(
      'div.bs-table',
      { 'data-testid': 'bs-table', 'data-phase': v.phase },
      isSetup ? readyChips : [fleetChips(ctx, v, theirSeat), fleetChips(ctx, v, mySeat)],
      tabs,
      isSetup ? (me === null ? null : h('div.bs-pane', {}, h('div.bs-title', {}, 'Your fleet'), myBoard)) : [
        h('div.bs-pane', { 'data-testid': 'bs-pane-their', ...(tab === 'their' ? {} : { hidden: true }) }, theirBoard),
        h('div.bs-pane', { 'data-testid': 'bs-pane-mine', ...(tab === 'mine' ? {} : { hidden: true }) }, myBoard),
      ],
      tray,
      setupButtons,
      h('div.event', { 'data-testid': 'bs-status' }, statusText(ctx, v, me, placedIds.size, targets.length)),
      note ? h('div.error', { 'data-testid': 'bs-note' }, note) : null,
      last ? h('div.muted.bs-last', { 'data-testid': 'bs-last' }, last) : null,
      fireBar,
      resign,
    );
  },
};
